import { isTauri } from "@/hooks/usePlatform";
import { getDataSource } from "@/lib/data";
import { injectInheritedAiEnvVars } from "./inherited-env";
import { useWorkspaceStore } from "@/stores/workspaceStore";
import { type Project, useWorldStore } from "@/stores/worldStore";

export type RuntimeEnvVars = Record<string, string>;

function resolveProjectPath(project: Project): string {
  // localPath already contains the full path (including subdirectory if any)
  return project.localPath?.trim() ?? "";
}

function normalizePath(input: string): string {
  return input
    .trim()
    .replace(/\\/g, "/")
    .replace(/\/+/g, "/")
    .replace(/\/+$/, "");
}

function isSameOrChildPath(basePath: string, targetPath: string): boolean {
  if (!basePath || !targetPath) return false;
  if (basePath === targetPath) return true;
  return targetPath.startsWith(`${basePath}/`);
}

/** Match a working directory to the best-fitting project by longest path prefix. */
export function resolveProjectIdForCwd(cwd: string, projects: Project[]): number | null {
  const normalizedCwd = normalizePath(cwd);
  if (!normalizedCwd) return null;

  let bestMatch: { id: number; score: number } | null = null;

  for (const project of projects) {
    const projectPath = normalizePath(resolveProjectPath(project));
    if (!projectPath) continue;
    if (!isSameOrChildPath(projectPath, normalizedCwd)) continue;

    const score = projectPath.length;
    if (!bestMatch || score > bestMatch.score) {
      bestMatch = { id: project.id, score };
    }
  }

  return bestMatch?.id ?? null;
}

async function loadSecretEnvForProjectId(projectId: number): Promise<RuntimeEnvVars> {
  if (!isTauri()) return {};
  if (useWorkspaceStore.getState().getActiveWorkspaceType() !== "local") return {};

  const ds = await getDataSource("local");
  const metadata = await ds.secrets.list(projectId);
  if (!Array.isArray(metadata) || metadata.length === 0) return {};

  const envVars: RuntimeEnvVars = {};
  for (const secret of metadata) {
    const name = typeof secret.name === "string" ? secret.name.trim() : "";
    const value = typeof secret.value === "string" ? secret.value : "";
    if (name && value) envVars[name] = value;
  }
  return envVars;
}

export async function loadProjectSecretEnv(projectId: number): Promise<RuntimeEnvVars> {
  return loadSecretEnvForProjectId(projectId);
}

async function injectWorkspaceGitCredentials(
  envVars: RuntimeEnvVars,
  projectId: number,
): Promise<void> {
  if (!isTauri()) return;
  try {
    // Resolve workspace ID from store or fallback to fetching world data
    let wsId: number | undefined;
    const storeProject = useWorldStore.getState().projects.find(p => p.id === projectId);
    if (storeProject?.workspaceId) {
      wsId = storeProject.workspaceId;
    } else {
      const ds = await getDataSource("local");
      const world = await ds.getWorldData();
      const dbProject = world.projects?.find((p: Project) => p.id === projectId);
      wsId = dbProject?.workspaceId;
    }
    if (!wsId) return;

    const wsKey = String(wsId);
    const { invoke } = await import("@tauri-apps/api/core");

    // PAT for git push/clone
    if (!envVars["GITHUB_TOKEN"] && !envVars["GH_TOKEN"]) {
      const pat = await invoke<string | null>("get_secret", { key: `gh_pat_ws_${wsKey}` }).catch(() => null);
      if (pat) {
        envVars["GITHUB_TOKEN"] = pat;
        envVars["GH_TOKEN"] = pat;
      }
    }

    // Git identity
    const gitName = await invoke<string | null>("get_secret", { key: `gh_git_name_ws_${wsKey}` }).catch(() => null);
    const gitEmail = await invoke<string | null>("get_secret", { key: `gh_git_email_ws_${wsKey}` }).catch(() => null);
    if (gitName) {
      envVars["GIT_AUTHOR_NAME"] = gitName;
      envVars["GIT_COMMITTER_NAME"] = gitName;
    }
    if (gitEmail) {
      envVars["GIT_AUTHOR_EMAIL"] = gitEmail;
      envVars["GIT_COMMITTER_EMAIL"] = gitEmail;
    }

    // Inline git credential helper via GIT_CONFIG_COUNT
    // This makes `git push`, `git clone` etc. use GITHUB_TOKEN automatically
    if (envVars["GITHUB_TOKEN"]) {
      envVars["GIT_CONFIG_COUNT"] = "2";
      envVars["GIT_CONFIG_KEY_0"] = "credential.https://github.com.helper";
      envVars["GIT_CONFIG_VALUE_0"] = "!f() { echo \"protocol=https\nhost=github.com\nusername=x-access-token\npassword=${GITHUB_TOKEN}\"; }; f";
      envVars["GIT_CONFIG_KEY_1"] = "credential.helper";
      envVars["GIT_CONFIG_VALUE_1"] = "";
    }
  } catch {
    // Non-critical
  }
}

export async function loadSecretEnvForCwd(cwd: string): Promise<RuntimeEnvVars> {
  if (!isTauri()) return {};
  if (useWorkspaceStore.getState().getActiveWorkspaceType() !== "local") return {};

  const localCwd = cwd.trim();
  if (!localCwd) return {};

  const worldProjects = useWorldStore.getState().projects;
  let projectId = resolveProjectIdForCwd(localCwd, worldProjects);

  if (projectId == null) {
    const ds = await getDataSource("local");
    const world = await ds.getWorldData();
    projectId = resolveProjectIdForCwd(localCwd, world.projects ?? []);
  }

  if (projectId == null) return {};

  const envVars = await loadSecretEnvForProjectId(projectId);

  await injectInheritedAiEnvVars(envVars);

  // Inject workspace git credentials into the terminal env
  await injectWorkspaceGitCredentials(envVars, projectId);

  return envVars;
}
